import { useCallback, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { legalDocumentsApi } from '@/lib/pillarApi';
import type { GetLegalDocumentResponse } from '@pillar-portal/api-types';
import { buildLegalDocumentIframeSrcDoc } from './legal-document-rendering';

export interface LegalDocumentReviewProps {
    legalDocumentUuid: string;
    title: string;
    /** Label of the acceptance button. */
    acceptLabel: string;
    acceptPending: boolean;
    onAccept: () => void;
    /** An extra confirmation the signer must give, such as signing authority. */
    confirmation?: {
        label: ReactNode;
        checked: boolean;
        onChange: (checked: boolean) => void;
    };
}

/**
 * Shows the exact text of a legal document and enables acceptance once the
 * reader has scrolled to its end. Shared by the personal and the Organization
 * signing workflows.
 */
export function LegalDocumentReview({
    legalDocumentUuid,
    title,
    acceptLabel,
    acceptPending,
    onAccept,
    confirmation,
}: LegalDocumentReviewProps) {
    const [reviewedDocumentUuid, setReviewedDocumentUuid] = useState<string | null>(null);
    const [reviewError, setReviewError] = useState<string | null>(null);
    const [reviewGeneration, setReviewGeneration] = useState(0);
    const legalDocumentFrameRef = useRef<HTMLIFrameElement | null>(null);
    const disposeReviewRef = useRef<() => void>(() => {});
    const bindLegalDocumentFrame = useCallback(
        (htmlIFrameElement: HTMLIFrameElement | null): void => {
            disposeReviewRef.current();
            legalDocumentFrameRef.current = htmlIFrameElement;
            setReviewedDocumentUuid(null);
            setReviewError(null);
        },
        []
    );
    const legalDocumentQuery = useQuery<GetLegalDocumentResponse>({
        queryKey: ['legalDocument', legalDocumentUuid],
        queryFn: async () => await legalDocumentsApi.getLegalDocument(legalDocumentUuid),
        retry: false,
        staleTime: 0,
    });

    const handleLegalDocumentLoad = (event: SyntheticEvent<HTMLIFrameElement>): void => {
        const legalDocumentFrame = event.currentTarget;
        if (legalDocumentFrame !== legalDocumentFrameRef.current) return;
        disposeReviewRef.current();
        setReviewedDocumentUuid(null);
        setReviewError(null);

        let disposed = false;
        let reviewComplete = false;
        const failReview = (): void => {
            if (disposed || legalDocumentFrameRef.current !== legalDocumentFrame) return;
            disposeReviewRef.current();
            setReviewedDocumentUuid(null);
            setReviewError(
                'Document review could not be observed. Reload the exact document and try again.'
            );
        };
        try {
            const legalDocument = legalDocumentFrame.contentDocument;
            const legalDocumentWindow = legalDocument?.defaultView;
            if (!legalDocument || !legalDocumentWindow || legalDocument.URL !== 'about:srcdoc') {
                failReview();
                return;
            }
            const isCurrentDocument = (): boolean =>
                !disposed &&
                legalDocumentFrameRef.current === legalDocumentFrame &&
                legalDocumentFrame.contentDocument === legalDocument &&
                legalDocument.URL === 'about:srcdoc';
            const lastLineVisible = (): boolean => {
                const scrollingElement =
                    legalDocument.scrollingElement ?? legalDocument.documentElement;
                const viewport =
                    legalDocument.compatMode === 'BackCompat'
                        ? legalDocument.body.clientHeight
                        : legalDocument.documentElement.clientHeight;
                return (
                    viewport > 0 &&
                    scrollingElement.scrollHeight > 0 &&
                    scrollingElement.scrollTop + viewport >= scrollingElement.scrollHeight - 8
                );
            };
            const observeReview = (): void => {
                if (disposed) return;
                try {
                    if (!isCurrentDocument()) {
                        failReview();
                        return;
                    }
                    if (reviewComplete || !lastLineVisible()) return;
                    reviewComplete = true;
                    setReviewedDocumentUuid(legalDocumentUuid);
                } catch {
                    failReview();
                }
            };
            // The child cannot execute scripts. The trusted parent observes its
            // real document instead of requiring executable sanitized srcdoc.
            const resizeObserver = new ResizeObserver(observeReview);
            // Following a link would navigate the frame away from the exact
            // document and revoke the review. Open external links in a new tab.
            const openLinkOutsideDocument = (event: MouseEvent): void => {
                const target = event.target;
                if (!(target instanceof legalDocumentWindow.Element)) return;
                const anchor = target.closest('a[href]');
                if (anchor === null) return;
                const href = anchor.getAttribute('href') ?? '';
                if (href.startsWith('#')) return;
                event.preventDefault();
                const linkUrl = z.string().url().safeParse(href);
                if (
                    linkUrl.success &&
                    (linkUrl.data.startsWith('https://') || linkUrl.data.startsWith('http://'))
                ) {
                    window.open(linkUrl.data, '_blank', 'noopener,noreferrer');
                }
            };
            const disposeReview = (): void => {
                disposed = true;
                legalDocument.removeEventListener('click', openLinkOutsideDocument);
                resizeObserver.disconnect();
                legalDocument.removeEventListener('scroll', observeReview);
                legalDocumentWindow.removeEventListener('scroll', observeReview);
                legalDocumentWindow.removeEventListener('resize', observeReview);
                legalDocumentWindow.removeEventListener('pagehide', failReview);
                legalDocumentWindow.removeEventListener('beforeunload', failReview);
            };
            disposeReviewRef.current = disposeReview;
            legalDocument.addEventListener('click', openLinkOutsideDocument);
            legalDocument.addEventListener('scroll', observeReview, { passive: true });
            legalDocumentWindow.addEventListener('scroll', observeReview, { passive: true });
            legalDocumentWindow.addEventListener('resize', observeReview);
            // Sandbox forbids top navigation, not self-navigation. Revoke the
            // old document's eligibility before leaving it and on every load.
            legalDocumentWindow.addEventListener('pagehide', failReview);
            legalDocumentWindow.addEventListener('beforeunload', failReview);
            resizeObserver.observe(legalDocument.documentElement);
            resizeObserver.observe(legalDocument.body);
            observeReview();
        } catch {
            failReview();
        }
    };

    const document = legalDocumentQuery.data?.legal_document ?? null;
    const srcDoc =
        document === null
            ? null
            : buildLegalDocumentIframeSrcDoc({
                  legalDocumentContentType: document.legal_document_content_type,
                  legalDocumentText: document.legal_document_text,
              });
    const reviewComplete = reviewedDocumentUuid === legalDocumentUuid;
    const confirmationMissing = confirmation !== undefined && !confirmation.checked;
    const acceptDisabledReason =
        document === null || legalDocumentQuery.isError
            ? 'Wait for the legal document to load.'
            : !reviewComplete
              ? 'Read the whole document and scroll all the way down to enable acceptance.'
              : confirmationMissing
                ? 'Confirm the statement above to enable acceptance.'
                : acceptPending
                  ? 'Please wait while your acceptance is processed.'
                  : null;

    return (
        <div className="space-y-4">
            {legalDocumentQuery.isLoading ? (
                <p>Loading document...</p>
            ) : legalDocumentQuery.isError ? (
                <div role="alert">
                    <p>The exact document could not be loaded.</p>
                    <Button className="mt-3" onClick={() => legalDocumentQuery.refetch()}>
                        Retry document
                    </Button>
                </div>
            ) : srcDoc === null ? (
                <p>The exact document is unavailable.</p>
            ) : (
                <iframe
                    key={`${legalDocumentUuid}:${reviewGeneration}`}
                    ref={bindLegalDocumentFrame}
                    title={title}
                    sandbox="allow-same-origin"
                    onLoad={handleLegalDocumentLoad}
                    className="h-[70vh] w-full rounded border"
                    srcDoc={srcDoc}
                />
            )}
            {reviewError === null ? null : (
                <div role="alert">
                    <p>{reviewError}</p>
                    <Button
                        onClick={() => {
                            disposeReviewRef.current();
                            setReviewedDocumentUuid(null);
                            setReviewError(null);
                            setReviewGeneration((generation) => generation + 1);
                        }}
                    >
                        Retry document review
                    </Button>
                </div>
            )}
            {confirmation === undefined ? null : (
                <label className="flex items-start gap-2 text-sm">
                    <input
                        type="checkbox"
                        className="mt-1"
                        checked={confirmation.checked}
                        onChange={(event) => confirmation.onChange(event.target.checked)}
                    />
                    <span>{confirmation.label}</span>
                </label>
            )}
            <span
                className="inline-flex"
                title={acceptDisabledReason ?? undefined}
                tabIndex={acceptDisabledReason === null ? undefined : 0}
            >
                <Button
                    disabled={acceptDisabledReason !== null}
                    aria-describedby={
                        acceptDisabledReason === null
                            ? undefined
                            : 'legal-acceptance-disabled-reason'
                    }
                    onClick={onAccept}
                >
                    {acceptLabel}
                </Button>
                {acceptDisabledReason === null ? null : (
                    <span id="legal-acceptance-disabled-reason" role="tooltip" className="sr-only">
                        {acceptDisabledReason}
                    </span>
                )}
            </span>
        </div>
    );
}
